import { computed, action, thunk } from 'easy-peasy'
import moment from 'moment'
import MeetingsService from '../services/meetings'
import { unescape } from '../utils/strings'
import { fieldsExtractor } from '../utils/object'
import { to } from '../utils/async'
import { isMeetingLocal, computeTopicDelta } from '../utils/meetings';

const initialState = {
  type: null,
  id: null,
  eventId: null, // used for linking
  userEventId: null,
  seriesMasterId: null, // used for linking, paired with masterRef
  masterRef: null, // used for linking, paired with seriesMasterId
  name: '',
  description: '',
  location: '',
  timezone: null,
  attendees: [],
  attendeesDirty: false, // used to track if the list of attendees was altered
  fetchedAttendees: [],
  startTime: null,
  endTime: null,
  externalLink: null, // used for linking
  workstreamId: null,
  recurrence: null,
  provider: 'Workboard', // used for linking
  calendarId: null,
  availableAttendees: [],
  pushToExternal: false,
  notifyParticipants: false,
  selectedMeeting: null,
  seriesUpdateType: 2,
  isLinked: null,
  isFetching: false,
  isFetchingLegacy: false,
  legacyData: null,
  topicCount: 0,
  status: '',
  commonEventId: null,
  commonSeriesId: null,
  userSeriesId: null,
  isConversationMeeting: false,
  isConversationMeetingCompleted: false,
  isConversationMeetingClosedByHr: false,
  isConversationMeetingNotRequired: false,
  conversationStatus: null,
  conversationMeeting: null
};

const I_UPDATE_MODEL = Object.freeze({
  allDay: null, // boolean
  calendarId: null, // string
  commonEventId: null, // string|null
  commonSeriesId: null, // string|null
  description: null, // string
  endTime: null, // number UTC
  eventId: null, // string|null - event id from Calender provider
  externalLink: null, // string
  id: null, // Agenda id, i.e. PK from wobo database
  location: null, // string
  masterRef: null, // string|null
  name: null, // string
  notifyParticipants: null, // boolean
  seriesUpdateType: null, // number
  provider: null, // string - Calendar provider
  pushToExternal: null, // boolean
  recurrence: null, // boolean
  seriesMasterId: null, // string|null
  startTime: null, // number UTC
  timezone: null, // string
  topicCount: null, // number
  type: null, // number - Discriminator for 1on1s vs team
  userEventId: null, // string|null
  userSeriesId: null, // string|null
  workstreamId: null, // string|null
});

const I_UPDATE_ATTENDEE_MODEL = Object.freeze({
  id: null, // number
  email: null, // string read-only,
  firstName: null, // string read-only,
  lastName: null, // string read-only,
  isAdmin: null, // boolean, // Refers to the `is_admin` property from tbl_meeting_invitees
  isLocal: null, // boolean read-only,
  // ^^^ 'true' if was invited locally, false if was invited in the 3d-party calendar GOOG/MSFT
  isOrganizer: null, // boolean read-only, // Refers to the event organizer in the calendar provider
  responseStatus: null, // Enum<'needsAction'|'declined'|'accepted'|'tentative'> read-only
});

const I_UPDATE_ATTENDEES_MODEL = Object.freeze({
  id: null, // number
  attendees: null, // array read-only
  seriesUpdateType: null, // number,
  userEventId: null, // string read-only,
  notifyParticipants: null, // boolean read-only,
  pushToExternal: null, // boolean read-only,
});

const byPositionThenCreatedDate = (topic1, topic2) => {
  const byPos = topic2.pos - topic1.pos;

  if (byPos === 0) {
    if (topic1.modified_at !== '0' || topic2.modified_at !== '0') {
      return parseInt(topic2.modified_at) - parseInt(topic1.modified_at);
    }

    return parseInt(topic2.create_at) - parseInt(topic1.create_at);
  }

  return byPos;
}

export const CONVERSATION_STATUS = {
  DELETED: 0,
  NOT_STARTED:  1,
  IN_PROGRESS:  2,
  COMPLETED:  3,
  ARCHIEVED: 4,
  DRAFT: 5
}

export const CONVERSATION_EVENT_STATUS = {
  DELETED: 0,
  CREATED:  1,
  COMPLETED:  2,
  INCOMPLETE:  3,
  NOT_REQUIRED: 4
}

export const CONVERSATION_EVENT_COMPLETE_BY = {
  MANAGER: 1,
  MEMBER: 2,
  HR: 4
}

export const CONVERSATION_EVENT_NOT_REQUIRED_BY = {
  MANAGER: 1,
  MEMBER: 2,
  SYSTEM: 3,
  HR: 4
}

export const getConversationData = (meetingResponse) => { 
  const isConversationMeeting = meetingResponse?.conversationMeetingData;
  let conversationMeeting = meetingResponse?.conversationMeetingData
  const isConversationMeetingCompleted = meetingResponse?.conversationMeetingData?.status == CONVERSATION_EVENT_STATUS.COMPLETED ?? false;
  const isConversationMeetingClosedByHr = meetingResponse?.conversationMeetingData?.status == CONVERSATION_EVENT_STATUS.INCOMPLETE ?? false;
  const isConversationMeetingNotRequired = meetingResponse?.conversationMeetingData?.status == CONVERSATION_EVENT_STATUS.NOT_REQUIRED ?? false;
  const conversationStatus = meetingResponse?.conversationMeetingData?.conversationStatus 

  if(conversationMeeting) {
    const attendee = meetingResponse?.attendees?.find((user) => !user.isOrganizer)
    let attendeeName = '';
    let attendeeStatus = '';
    if (attendee) {
      attendeeName = `${attendee?.firstName} ${attendee?.lastName}`;
      attendeeStatus = ['-1', '0'].includes(attendee?.status) ? '(Deactivated)' : '';
    }

    conversationMeeting = {
      ...conversationMeeting,
      dueDate: new Date(conversationMeeting.dueDate).getTime() / 1000,
      completedDate: new Date(conversationMeeting.completedDate).getTime() / 1000,
      notRequiredDate: new Date(conversationMeeting.notRequiredDate).getTime() / 1000, 
      attendeeName,
      attendeeStatus,
    }
  }

  return { 
    isConversationMeeting, 
    conversationMeeting, 
    isConversationMeetingCompleted,
    isConversationMeetingClosedByHr, 
    isConversationMeetingNotRequired,
    conversationStatus
  }
};

export const showPace1on1 = (meeting) => {
  return meeting.type == 1
    && !window.wbConfig?.disableMeetingsPaceMeter
    && (meeting?.legacyData?.epa_coaching_text ?? false)
    && (
      // 1 attribute has to enabled
      meeting.legacyData.epa_coaching_text.align.enabled
      || meeting.legacyData.epa_coaching_text.perform.enabled
      || meeting.legacyData.epa_coaching_text.comp.enabled
      || meeting.legacyData.epa_coaching_text.engage.enabled
      || meeting.legacyData.epa_coaching_text.attribute5.enabled
      || (meeting?.legacyData?.meeting_pace?.custom_pace_ratings?.length > 0 
          && meeting.legacyData.epa_coaching_text.custom_pace_attributes?.length > 0
          && meeting.legacyData.epa_coaching_text.custom_pace_attributes.some(attr => attr.enabled))
      || (meeting?.legacyData?.meeting_pace_selected?.custom_pace_ratings?.length > 0
          && meeting.legacyData.epa_coaching_text.custom_pace_attributes?.length > 0
          && meeting.legacyData.epa_coaching_text.custom_pace_attributes.some(attr => attr.enabled))
    )
};

export const showPaceCheckIn = (paceRatings, conversationMeeting) => {
  return paceRatings == 1
  && !window.wbConfig?.disableMeetingsPaceMeter
  && (conversationMeeting?.meetingPace.epa_coaching_text ?? false)
  && (
    conversationMeeting?.meetingPace.epa_coaching_text.align.enabled
    || conversationMeeting?.meetingPace.epa_coaching_text.perform.enabled
    || conversationMeeting?.meetingPace.epa_coaching_text.comp.enabled
    || conversationMeeting?.meetingPace.epa_coaching_text.engage.enabled
    || conversationMeeting?.meetingPace.epa_coaching_text.attribute5.enabled
    || (conversationMeeting?.meetingPace?.meeting_pace?.custom_pace_ratings?.length > 0
        && conversationMeeting.meetingPace.epa_coaching_text.custom_pace_attributes?.length > 0
        && conversationMeeting.meetingPace.epa_coaching_text.custom_pace_attributes.some(attr => attr.enabled))
    || (conversationMeeting?.meetingPace?.meeting_pace_selected?.custom_pace_ratings?.length > 0
        && conversationMeeting.meetingPace.epa_coaching_text.custom_pace_attributes?.length > 0
        && conversationMeeting.meetingPace.epa_coaching_text.custom_pace_attributes.some(attr => attr.enabled))
  )
}

const instantiateMeetingModel = () => ({
  // state
  ...initialState,

  // derived state
  computedPayload: computed((state) => {
    const attendees = state.attendees.map(
      (attendee) => fieldsExtractor(attendee, I_UPDATE_ATTENDEE_MODEL),
    );

    return fieldsExtractor(state, I_UPDATE_MODEL, { attendees });
  }),

  isValidOneOnOne: computed((state) => {
    if (!state.is1on1) {
      return false
    }
    if (!state.attendees.length || state.attendees.length !== 2) {
      return false
    }
    if (state.isLinked === null) {
      return false
    }
    if (state.isLinked && state.eventId) {
      return true
    } if (!state.isLinked) {
      return true
    }
    return false
  }),

  isValidMeeting: computed((state) => !state.is1on1 && !!state.name),
  isValid: computed((state) => state.isValidOneOnOne || state.isValidMeeting), // TODO

  // actions
  setType: action((state, payload) => { state.type = payload }),
  setId: action((state, payload) => { state.id = payload }),
  setEventId: action((state, payload) => { state.eventId = payload }),
  setUserEventId: action((state, payload) => { state.userEventId = payload }),
  setSeriesMasterId: action((state, payload) => { state.seriesMasterId = payload }),
  setName: action((state, payload) => { state.name = payload }),
  setTimezone: action((state, payload) => { state.timezone = payload }),
  setDescription: action((state, payload) => { state.description = payload }),
  setLocation: action((state, payload) => { state.location = payload }),
  setLegacyData: action((state, payload) => {
    if (payload && payload.meeting_topics) {
      payload.meeting_topics.sort(byPositionThenCreatedDate);
      const oldLegacyData = state.legacyData ?? state.lastLegacyData;
      const legacyDataDelta = computeTopicDelta(oldLegacyData, payload)
      state.legacyDataDelta = legacyDataDelta;
      state.lastLegacyData = null;
    }
    state.legacyData = state.legacyData ? { ...state.legacyData, meeting_topics: payload.meeting_topics, takeSmartWoboId: payload?.takeSmartWoboId } : payload;
  }),
  setLegacyDataDelta: action((state, payload) => { state.legacyDataDelta = payload }),

  // cache attendees so we can overwrite all changes when they cancel
  // the add attendees dropdown
  setFetchedAttendees: action((state, payload) => {
    state.fetchedAttendees = JSON.stringify(payload)
  }),

  setStartTime: action((state, payload) => { state.startTime = payload }),
  setEndTime: action((state, payload) => { state.endTime = payload }),
  setWorkstreamId: action((state, payload) => { state.workstreamId = payload }),
  setRecurrence: action((state, payload) => { state.recurrence = payload }),
  setSeriesUpdateType: action((state, payload) => { state.seriesUpdateType = payload }),
  setIsLinked: action((state, payload) => { state.isLinked = payload }),
  setIsFetching: action((state, payload) => { state.isFetching = payload }),
  setIsFetchingLegacy: action((state, payload) => { state.isFetchingLegacy = payload }),
  setTopicCount: action((state, payload) => { state.topicCount = payload }),
  setConversationMeetingComplete: action((state, payload) => { 
    state.isConversationMeetingCompleted = payload 
    state.isConversationMeetingClosedByHr = payload
    state.isConversationMeetingNotRequired = payload
    state.conversationStatus = payload
    if (state.conversationMeeting) {
      state.conversationMeeting.completedDate = moment().unix()
    }
  }),
  setConversationMeetingPaceRatingsSync: action((state, payload) => {
    // Validate payload
    if (!payload || typeof payload !== 'object') {
      console.warn('setConversationMeetingPaceRatingsSync: Invalid payload received', payload);
      return;
    }

    // Validate conversationMeeting exists
    if (!state.conversationMeeting) {
      console.warn('setConversationMeetingPaceRatingsSync: No conversation meeting found!');
      return;
    }

    // Initialize meetingPace if it doesn't exist
    if (!state.conversationMeeting.meetingPace) {
      state.conversationMeeting.meetingPace = {};
    }

    // Validate required fields
    const { managerId } = state.conversationMeeting;
    const { owner: actorId } = payload;

    if (!managerId || !actorId) {
      console.warn('setConversationMeetingPaceRatingsSync: Missing managerId or actorId', { managerId, actorId });
      return;
    }

    const isManager = managerId === actorId;

    // Store ratings based on user role
    if (isManager) {
      state.conversationMeeting.meetingPace.meeting_pace = payload;
    } else {
      state.conversationMeeting.meetingPace.meeting_pace_selected = payload;
    }
  }),
  setConversationMeetingPaceRatings: thunk(async (actions, payload = {}) => {
    actions.setConversationMeetingPaceRatingsSync(payload);
  }),
  setMeeting: action((state, payload) => {
    if (state.legacyData) {
      state.lastLegacyData = { ...state.legacyData }
    }

    Object
      .keys(initialState)
      .forEach((key) => {
        state[key] = payload && payload[key] !== undefined ? payload[key] : initialState[key]
      })

    state.availableAttendees = payload.attendees?.filter((user) => !user.isOrganizer) || []
  }),

  resetMeeting: action((state) => {
    Object
      .keys(initialState)
      .forEach((key) => {
        state[key] = initialState[key]
      })
  }),

  // make sure users are not able to delete some important information:
  // timezone, start time, end time
  validatePopulate: thunk(async (actions, payload = {}, { getState, getStoreState }) => {
    const meeting = getState()
    const { app } = getStoreState()
    const defaultTimezone = (app.user && app.user.timezone) || moment.tz.guess()

    actions.setMeeting({
      ...meeting,
      timezone: meeting.timezone ? meeting.timezone : defaultTimezone,
      startTime: meeting.startTime ? meeting.startTime : moment().unix(),
      endTime: meeting.endTime ? meeting.endTime : moment().add(30, 'minutes').unix(),
    })
  }),

  setNotifyParticipants: action((state, payload) => { state.notifyParticipants = payload }),
  setPushToExternal: action((state, payload) => { state.pushToExternal = payload }),
  setSelectedMeeting: action((state, payload) => { state.selectedMeeting = payload }),

  addAvailableAttendees: action((state, payload) => {
    const availableAttendeesIds = state.availableAttendees.map((p) => p.id)
    const attendeeIds = state.availableAttendees.map((p) => p.id)

    state.availableAttendees = ([
      ...state.availableAttendees,
      ...payload.filter((p) => !availableAttendeesIds.includes(p.id)),
    ])

    state.attendees = [
      ...state.attendees,
      ...payload.filter((p) => !attendeeIds.includes(p.id)),
    ]
  }),
  setAttendees: action((state, payload) => {
    state.attendees = [...payload]
    state.attendeesDirty = false
  }),
  addAttendee: action((state, payload) => {
    state.attendees = [...state.attendees, payload]
  }),
  addAttendees: action((state, attendees = []) => {
    state.attendees = [...state.attendees, ...attendees]
  }),
  editAttendee: action((state, payload) => {
    const index = state.attendees.findIndex(a => a.id === payload.id)
    state.attendees.splice(index, 1, payload)
    state.attendeesDirty = true
  }),
  addAllAttendeesAvailable: action((state) => {
    state.attendees = [...state.availableAttendees]
  }),
  setAvailableAttendees: action((state) => {
    state.availableAttendees = [...state.attendees]
  }),
  removeAttendee: action((state, payload) => {
    state.attendees = state.attendees.filter((p) => p.id !== payload.id)
    state.attendeesDirty = true
  }),
  removeAllAttendees: action((state) => {
    state.attendees = []
    state.attendeesDirty = false
  }),

  is1on1: computed(state => state.type == 1),

  // cannot rely on state.source as remote merged with local will keep a source of local
  isLocal: computed(state => isMeetingLocal(state)),

  showPace: computed(state =>
    state.is1on1
    && !window.wbConfig?.disableMeetingsPaceMeter
    && (state?.legacyData?.epa_coaching_text ?? false)
    && (
      // 1 attribute has to enabled
      state.legacyData.epa_coaching_text.align.enabled
      || state.legacyData.epa_coaching_text.perform.enabled
      || state.legacyData.epa_coaching_text.comp.enabled
      || state.legacyData.epa_coaching_text.engage.enabled
      || state.legacyData.epa_coaching_text.attribute5.enabled
    )
  ),


  // operations
  load: thunk(async (actions, id) => {
    actions.setIsFetching(true)
    const [err, response] = await to(MeetingsService.get(id))
    if (response && response.data && response.data.data) {
      const { data: { data: meeting } } = response
      if (err) {
        actions.setIsFetching(false)
        console.error(err)
        return [err, null]
      }
      
      const { isConversationMeeting, conversationMeeting, isConversationMeetingCompleted, isConversationMeetingClosedByHr, isConversationMeetingNotRequired, conversationStatus } = getConversationData(meeting);
      
      actions.setMeeting({
        ...meeting,
        isConversationMeeting,
        conversationMeeting,
        isConversationMeetingCompleted,
        isConversationMeetingClosedByHr,
        isConversationMeetingNotRequired,
        conversationStatus,
        name: unescape(meeting.name),
        location: unescape(meeting.location),
      })

      actions.setLegacyData(meeting.legacyData)
      actions.setFetchedAttendees(meeting.attendees)
      actions.setIsFetching(false)
      return [null, {...meeting, isConversationMeeting, conversationMeeting, isConversationMeetingCompleted, isConversationMeetingClosedByHr, isConversationMeetingNotRequired, conversationStatus }]
    }
    return ['data is misformatted', null]
  }),

  latest: thunk(async (actions, id) => {
    actions.setIsFetching(true)
    const [err, response] = await to(MeetingsService.getLatest(id))
    if (response && response.data && response.data.data) {
      const { data: { data: meeting } } = response
      if (err) {
        actions.setIsFetching(false)
        console.error(err)
        return [err, null]
      }
      actions.setMeeting({
        ...meeting,
        name: unescape(meeting.name),
        location: unescape(meeting.location),
      })
      actions.setFetchedAttendees(meeting.attendees)
      actions.setIsFetching(false)
      return [null, meeting]
    }
    return ['data is misformatted', null]
  }),

  getMeetNow: thunk((actions, payload = {}, { getState, getStoreState }) => {
    const { app } = getStoreState()
    return {
      name: 'New Meeting',
      startTime: moment().unix(),
      endTime: moment().add(30, 'minutes').unix(),
      attendees: [{ ...app.user, isAdmin: true, isOrganizer: true }],
      type: 3,
      userEventId: null,
    }
  }),

  patch: thunk(async (
    actions,
    param,
    { getState, getStoreActions }
  ) => {
    const payload = param.payload || {}
    const model = param.model || {}
    const { computedPayload, legacyData } = getState()
    const { meetings } = getStoreActions()
    meetings.setCacheIsValid(false)

    const purifiedObject = Object.keys(model).length ? { ...model } : { ...computedPayload }
    // prepare a meeting object for a patch
    // 1. patch should only include the attributes we wish to update
    // 2. any attributes which are not supposed to be in the payload are excluded
    const meeting = {}
    for (const k in payload) {
      if (k in purifiedObject) {
        meeting[k] = payload[k]
      }
    }

    const [errUpdate, res] = await to(MeetingsService.update(meeting))

    if (errUpdate) {
      console.error(errUpdate)
      return [errUpdate, null]
    }
    if (!res || !res.data || !res.data.data) {
      return [errUpdate || 'Empty response', null]
    }
    const { data: { data: meetingResponse } } = res

    actions.setMeeting({
      ...meetingResponse,
      legacyData,
    })
    actions.setFetchedAttendees(meetingResponse.attendees)
    return [null, meetingResponse]
  }),

  patchAttendees: thunk(async (actions, param = {}) => {
    const { payload } = param
    return actions.patch({ payload, model: I_UPDATE_ATTENDEES_MODEL })
  }),

  update: thunk(async (actions, payload = {}, { getState, getStoreActions }) => {
    const { computedPayload, legacyData } = getState()
    const { meetings } = getStoreActions()
    meetings.setCacheIsValid(false)
    const meeting = { ...computedPayload, ...payload }
    const [errUpdate, res] = await to(MeetingsService.update(meeting))

    if (!res || !res.data || !res.data.data) {
      return [errUpdate || 'Empty response', null]
    }
    const { data: { data: meetingResponse } } = res
    if (errUpdate) {
      console.error(errUpdate)
      return [errUpdate, null]
    }

    const { isConversationMeeting, conversationMeeting, isConversationMeetingCompleted, isConversationMeetingClosedByHr, isConversationMeetingNotRequired, conversationStatus } = getConversationData(meetingResponse);

    actions.setMeeting({
      ...meetingResponse,
      isConversationMeeting,
      conversationMeeting,
      isConversationMeetingCompleted,
      isConversationMeetingClosedByHr,
      isConversationMeetingNotRequired,
      conversationStatus,
      name: unescape(meeting.name),
      location: unescape(meeting.location),
      legacyData,
    })
    actions.setFetchedAttendees(meetingResponse.attendees)
    return [null, {...meetingResponse, isConversationMeeting, conversationMeeting, isConversationMeetingCompleted, isConversationMeetingClosedByHr, isConversationMeetingNotRequired, conversationStatus}]
  }),

  updateName: thunk(async (actions, name, { getState }) => {
    const meeting = getState()
    if (name !== meeting.name) {
      const [err, _] = await to(actions.update({ name }))
      if (err) {
        console.error(err)
      }
      return !!err
    }
    return false
  }),

  create: thunk(async (actions, payload = {}, { getState, getStoreActions }) => {
    const [err, { data: { data: meeting } }] = await to(MeetingsService.create({ ...payload }))
    if (err) {
      console.error(err)
      return [err, null]
    }
    actions.setMeeting(meeting)
    actions.setFetchedAttendees(meeting.attendees)
    const { meetings } = getStoreActions()
    meetings.setCacheIsValid(false)
    return [null, meeting.id]
  }),
  fetchLegacyData: thunk(async (actions, payload = {}, { getState }) => {
    const meeting = getState()
    if (!meeting.isFetchingLegacy) {
      actions.setIsFetchingLegacy(true)
      const r1 = await to(MeetingsService.getLegacyData(payload))
      const [err, { data: response }] = r1
      actions.setIsFetchingLegacy(false)
      if (err) {
        console.error(err)
        return [err, null]
      }
      actions.setLegacyData(response.data)
      return [null, response.data]
    }
  }),

  fetch: thunk(async (actions, id, { getState, getStoreState }) => {
    const state = getState()
    actions.setIsFetching(true)
    const cachedMeetings = getStoreState().meetings.meetings
    let meeting = cachedMeetings.find((m) => m.id === id)

    if (!meeting && !state.isFetching) {
      const [err, res] = await to(MeetingsService.get(id))
      if (err) {
        console.error(err)
        actions.setIsFetching(false)
        return [err, null]
      }
      meeting = res.data.data
    }

    actions.addToCache(meeting)
    actions.setFetchedAttendees(meeting.attendees)
    actions.setIsFetching(false)

    return [null, meeting]
  }),
  addToCache: thunk((actions, payload, { getStoreActions }) => {
    const addToMeetings = getStoreActions().meetings.addMeeting

    addToMeetings(payload)
  }),
})

export default instantiateMeetingModel
